export function convertToUtcString(utcString: string): string {
  return utcString.endsWith("Z") ? utcString : utcString + "Z";
}

const SHORT_DATE_MEDIUM_TIME_FORMATTER = new Intl.DateTimeFormat("en", {
  dateStyle: "short",
  timeStyle: "medium"
});

/**
 * Safely formats a backend timestamp string for display. Avoids RangeError in Safari
 * when the value is invalid or not parseable (e.g. non-ISO or missing timezone).
 * @param rawTimestamp - Backend timestamp string (e.g. ISO or date-only)
 * @param fallback - String to return when timestamp is missing or invalid (default "")
 * @returns Formatted date/time string or fallback
 */
export function formatTimestampSafe(rawTimestamp: string | null | undefined, fallback = ""): string {
  if (rawTimestamp == null || String(rawTimestamp).trim() === "") {
    return fallback;
  }
  const normalized = convertToUtcString(String(rawTimestamp).trim());
  const date = new Date(normalized);
  if (!Number.isFinite(date.getTime())) {
    return fallback;
  }
  return SHORT_DATE_MEDIUM_TIME_FORMATTER.format(date);
}

export function getLocaleTimeString(utcString: string): string {
  const normalized = convertToUtcString(utcString);
  let createdAt = new Date(normalized);
  if (!Number.isFinite(createdAt.getTime())) {
    // Fallback for inputs that are already browser-formatted date strings.
    createdAt = new Date(utcString);
  }
  if (!Number.isFinite(createdAt.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat("en", {
    dateStyle: "short",
    timeStyle: "medium"
  }).format(createdAt);
}

export function formatDateMMDDYYYY(date: string): string {
  // Parse the date string as local date to avoid timezone shifts
  const [year, month, day] = date.split("-").map(Number);
  const dateObj = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  return dateObj.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" });
}

const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric"
});

/**
 * Formats a date string as a short human-readable date (e.g. "Jan 1, 2026").
 * Returns the original string when the input is not a valid date.
 */
export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return SHORT_DATE_FORMATTER.format(date);
}

/**
 * Formats a Date object as a localized time string using the user's browser locale
 * @param date - The Date object to format
 * @returns Localized time string (e.g., "3:45:30 PM" for en-US, "15:45:30" for de-DE)
 */
export function getLocalizedTimeString(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    timeStyle: "short"
  }).format(date);
}

/**
 * Formats a Date object as a localized date and time string using the user's browser locale
 * @param date - The Date object to format
 * @returns Localized date and time string (e.g., "12/31/2023, 3:45:30 PM" for en-US)
 */
export function getLocalizedDateTimeString(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "short",
    timeStyle: "medium"
  }).format(date);
}

const COMPACT_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit"
});

/**
 * Formats a Date as a compact locale-aware date string (e.g. "01/07/26" for en-US).
 * Uses the browser's locale so the order of month/day/year adapts to the user.
 *
 * NOTE: Unlike {@link formatDateMMDDYYYY} (which takes a date string, uses a
 * 4-digit year, and is hardcoded to en-US), this function accepts a Date object,
 * uses a 2-digit year, and respects the user's browser locale.
 */
export function formatDateForDisplay(date: Date): string {
  return COMPACT_DATE_FORMATTER.format(date);
}

/**
 * Formats a start–end date range for display using the user's locale.
 * Example (en-US): "01/07/26 – 04/06/26"
 */
export function formatDateRangeDisplay(start: Date, end: Date): string {
  return `${formatDateForDisplay(start)}\u2009–\u2009${formatDateForDisplay(end)}`;
}
